import { useCallback, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "react-query";
import { Badge, Button, Text } from "@radix-ui/themes";
import { ColumnDef } from "@tanstack/react-table";
import { PageEmptyState } from "@/components/page-empty-state/PageEmptyState";
import { Link } from "@/components/shared/link/Link";
import { SimpleTable } from "@/v2/components/table/SimpleTable";
import { dateTimeCell, statusCell } from "@/v2/components/table/cellRenderers";
import { apiResolver, useApi } from "@/api/useApi";
import { useReportsTracking } from "./useReportsTracking";
import { TReportDisplay } from "./index.types";
import { FeatureAccessContext } from "../../context/featureAccess";
import styles from "./styles.module.scss";

interface ReportRow {
  ticketId: string;
  createdAt: string;
  startTime: string;
  agentAssigned?: string;
  status: string;
  errors: number;
  browserName: string;
  os: string;
}

const columns: ColumnDef<ReportRow>[] = [
  {
    header: "Ticket",
    accessorKey: "ticketId",
    cell: ({ row }) => {
      return (
        <Text size={"2"} color="gray" weight="medium" highContrast>
          #{row.original.ticketId.split("::")[1]}
        </Text>
      );
    },
  },
  {
    header: "Created",
    accessorKey: "createdAt",
    cell: dateTimeCell,
  },
  {
    header: "Started",
    accessorKey: "startTime",
    cell: dateTimeCell,
  },
  {
    header: "Assignee",
    accessorKey: "agentAssigned",
  },
  {
    header: "Status",
    accessorKey: "status",
    cell: statusCell,
  },
  {
    header: "Errors",
    accessorKey: "errors",
    cell: ({ getValue }) => {
      const value = getValue<number | undefined>();

      if (value == null || value === 0) {
        return <></>;
      }

      return (
        <Badge size={"2"} color={value > 5 ? "red" : "green"}>
          {value}
        </Badge>
      );
    },
    meta: {
      className: "w-[80px]",
    },
  },
  {
    header: "Browser",
    accessorKey: "browserName",
    cell: ({ getValue }) => {
      const value = getValue<string | undefined>();

      if (value == null) {
        return <></>;
      }

      return (
        <Badge size={"2"} color="gray">
          {value}
        </Badge>
      );
    },
  },
  {
    header: "OS",
    accessorKey: "os",
    cell: ({ getValue }) => {
      const value = getValue<string | undefined>();

      if (value == null) {
        return <></>;
      }

      return (
        <Badge size={"2"} color={value === "Windows" ? "blue" : "purple"}>
          {value}
        </Badge>
      );
    },
    meta: {
      className: "w-[88px]",
    },
  },
];

export const Reports = () => {
  const navigate = useNavigate();
  const { api } = useApi();
  const { features } = useContext(FeatureAccessContext);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["session-reports"],
      queryFn: async ({ pageParam = 0 }) => {
        return apiResolver<{ reports: TReportDisplay[]; nextPage?: number }>(
          api.get(`/sessions-api/reports?page=${pageParam}`)
        );
      },
      getNextPageParam: (lastPage) => lastPage?.nextPage || undefined,
      enabled: true,
    });

  const { trackReportViewed } = useReportsTracking();

  const flatData = useMemo<TReportDisplay[]>(
    () => data?.pages?.flatMap((page) => page.reports) ?? [],
    [data]
  );

  const rows = useMemo<ReportRow[]>(() => {
    return flatData.map((item: TReportDisplay) => ({
      ticketId: item.ticketId,
      createdAt: item.createdAt,
      startTime: item.startTime,
      agentAssigned: item.agentAssigned?.email ?? "",
      status: item.status,
      errors: item.networkEvents
        ? item.networkEvents.filter(
            (event) => event.value.response.status >= 300
          ).length
        : 0,
      browserName: item?.session?.browserInfo?.browserName,
      os: item?.session?.browserInfo?.os,
    }));
  }, [flatData]);

  const handleRowClick = useCallback(
    (row: ReportRow) => {
      navigate(`/report/${row.ticketId}`);
    },
    [navigate]
  );

  // Track dashboard viewed
  useEffect(() => {
    trackReportViewed("dashboard", "reports");
  }, [trackReportViewed]);

  if (!features.sessions) {
    return (
      <PageEmptyState
        title={"Customer Sessions"}
        description={
          "No more chasing screenshots—gain complete visibility! Put customer sessions at the heart of every resolution, making support seamless and effective."
        }
        svg={"sessions-report"}
      >
        <div>
          <Link
            openInNewTab
            to={
              "https://calendly.com/nadav-a6u/cx-2028-quack-ai-discovery-clone-1?from=slack"
            }
          >
            Get Access
          </Link>
        </div>
      </PageEmptyState>
    );
  }

  if (rows.length === 0 && !isLoading) {
    return (
      <PageEmptyState
        title={"No session recordings yet."}
        description={
          "Once visitors start interacting with your site, you’ll see recordings here to help you understand their journey. "
        }
        svg={"sessions-report"}
      />
    );
  }

  return (
    <div className={styles.reportsTableContainer}>
      <div className="rounded-md overflow-hidden shadow-md border">
        {/* <GDashboardTable
        dataArray={allData}
        selectedTopic={0}
        handleRowClick={(rowId) => (window.location.href = `/report/${rowId}`)}
        columns={columns}
        onNextPage={() => {
          setPage(page + 1);
        }}
        nextPage={reports && reports.nextPage !== null}
        loadMore={isLoading}
        disableSelected
        loading={reports === undefined && allData.length === 0 && isLoading}
        firstColumnWidth="15%"
      /> */}

        <SimpleTable
          data={rows}
          columns={columns}
          loading={isLoading}
          onRowClicked={handleRowClick}
        />
      </div>

      {hasNextPage && (
        <div className="pt-4 flex justify-center">
          <Button
            variant="soft"
            color="gray"
            highContrast
            disabled={isFetchingNextPage}
            loading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            Load More
          </Button>
        </div>
      )}
    </div>
  );
};
